/**
 * Non-React helpers behind the login screen. Extracted verbatim from
 * app/(auth)/login/page.tsx (OPL-4289) — every URL, storage key, toast string
 * and log call here is identical to what the page ran before.
 */

import { isLegacyBackup, isType42Backup } from "bitcoin-backup";
import { toast } from "sonner";
import type { OAuthProvider } from "@/components/oauth-provider-buttons";
import { fetchLoginBackupStatus } from "@/lib/api/login-client";
import {
	completeRecoveryMethod,
	rememberSuccessfulSignIn,
} from "@/lib/auth/last-sign-in-method";
import { signInWithBackup } from "@/lib/auth-flows";
import {
	hasIncompleteInventory,
	INCOMPLETE_INVENTORY_KEY,
} from "@/lib/bap/backup-inventory";
import { markCloudBackupStale } from "@/lib/bap/cloud-refresh";
import {
	decryptIdentityBackup,
	encryptIdentityBackup,
	type IdentityBackup,
	isSeedIdentityBackup,
	validateIdentityBackup,
} from "@/lib/bap/identity-backup";
import {
	downloadFile,
	extractIdentityFromBackup,
	reissueLegacyBackupIfNeeded,
} from "@/lib/bap/utils";

import { logger } from "@/lib/logger";
import { STORAGE_KEYS } from "@/lib/storage-keys";
import { markPrfBackupStale } from "@/lib/webauthn/prf-invalidate";
import { preserveImportedVaultBackup } from "@/lib/webauthn/prf-setup";

// Constants for magic numbers
export const MIN_PASSWORD_LENGTH = 8;
export const AUTO_SUBMIT_DELAY = 100;
export const BASE64_MODULO = 4;

// Top-level regex for performance (js-hoist-regexp)
export const BASE64_REGEX = /^[A-Za-z0-9+/=]+$/;
export const MAC_PLATFORM_REGEX = /Mac|iPhone|iPad|iPod/;

export type PostSignInResult = "redirecting" | "signer_return";

export interface LoginOAuthParams {
	clientId: string | null;
	redirectUri: string | null;
	state: string | null;
	codeChallenge: string | null;
	codeChallengeMethod: string | null;
	scope: string | null;
	responseType: string | null;
}

export type SyncStepUpdater = (
	id: string,
	status: "in_progress" | "completed" | "error",
	error?: string
) => void;

/** Validate backup structure */
export function validateBackupStructure(data: unknown): data is IdentityBackup {
	try {
		validateIdentityBackup(data);
		return true;
	} catch {
		return false;
	}
}

/** Build an OAuth client-not-found error message */
export function getOAuthClientErrorMessage(redirectUri: string | null): string {
	if (!redirectUri) {
		return "OAuth client not found. The requested application is not registered.";
	}

	const hostname = new URL(redirectUri).hostname;
	const isSelfOAuth =
		typeof window !== "undefined" &&
		redirectUri.startsWith(window.location.origin);

	if (isSelfOAuth) {
		return (
			"Self-authentication not configured. " +
			"This authentication server is not registered as an OAuth client of itself. " +
			"Contact your administrator to register this server as an OAuth client, or use the direct sign-in page instead."
		);
	}

	return (
		`OAuth client not registered. The application "${hostname}" is not registered as an OAuth client. ` +
		"The application owner needs to register their OAuth client before users can authenticate."
	);
}

/** Build OAuth authorize URL from search params */
export function buildAuthorizeUrl(
	origin: string,
	params: {
		clientId: string;
		redirectUri: string;
		state: string | null;
		codeChallenge: string | null;
		codeChallengeMethod: string | null;
		scope: string | null;
		responseType: string | null;
	}
): URL {
	const url = new URL("/api/auth/oauth2/authorize", origin);
	url.searchParams.set("client_id", params.clientId);
	url.searchParams.set("redirect_uri", params.redirectUri);
	if (params.state) {
		url.searchParams.set("state", params.state);
	}
	if (params.codeChallenge) {
		url.searchParams.set("code_challenge", params.codeChallenge);
	}
	if (params.codeChallengeMethod) {
		url.searchParams.set("code_challenge_method", params.codeChallengeMethod);
	}
	if (params.scope) {
		url.searchParams.set("scope", params.scope);
	}
	if (params.responseType) {
		url.searchParams.set("response_type", params.responseType);
	}
	return url;
}

/** Create a sync progress callback for updating sync step UI */
export function createSyncProgressCallback(updateStep: SyncStepUpdater) {
	return (
		step: string,
		status: "in_progress" | "completed" | "error",
		error?: string
	) => {
		if (
			step.includes("Discovering") ||
			step.includes("Found") ||
			step.includes("identity") ||
			step.includes("identities")
		) {
			updateStep("discover", status, error);
		} else if (step.includes("Registering") || step.includes("Registered")) {
			updateStep("register", status, error);
		} else if (
			step.includes("blockchain") ||
			step.includes("Scanning") ||
			step.includes("Checking")
		) {
			updateStep("blockchain", status, error);
		}
	};
}

export function notifySignerReturnIfRequested(): boolean {
	const params = new URLSearchParams(window.location.search);
	if (params.get("signer_return") !== "1" || !window.opener) {
		return false;
	}

	window.opener.postMessage(
		{ type: "SIGMA_SIGNIN_COMPLETE" },
		window.location.origin
	);
	window.setTimeout(() => window.close(), 100);
	return true;
}

export function appendProviderToCallbackURL(
	callbackURL: string,
	provider: OAuthProvider
): string {
	const url = new URL(callbackURL);
	url.searchParams.set("provider", provider);
	return url.toString();
}

/** Re-extract identity after sign-in and optionally create member backups */
// biome-ignore lint/suspicious/useAwait: moved verbatim from app/(auth)/login/page.tsx (OPL-4289). The async signature is the contract its awaiting callers rely on, and it keeps a synchronous sessionStorage throw as a rejected promise; the rule was only silent before because Next's preset disables it inside page.tsx.
export async function postSignInIdentitySync(
	identity: { id: string; address: string },
	_password: string,
	_result: {
		syncedIdentities?: Array<{ bapId: string; counter: number; name?: string }>;
	}
): Promise<{ id: string; address: string }> {
	const updatedBackup = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	if (!updatedBackup) {
		return identity;
	}

	try {
		const parsedBackup = JSON.parse(updatedBackup) as IdentityBackup;
		const finalIdentity = extractIdentityFromBackup(parsedBackup);
		logger.info({ operation: "post_sign_in_identity_sync" }, "Identity synced");

		return finalIdentity;
	} catch {
		logger.warn(
			{ operation: "post_sign_in_identity_sync" },
			"Failed to re-extract identity; using original"
		);
		return identity;
	}
}

/** Decrypt and validate a backup from localStorage, updating sync steps */
export async function decryptAndValidateBackup(
	encryptedBackup: string,
	pwd: string,
	updateStep: SyncStepUpdater,
	validateFn: (data: unknown) => data is IdentityBackup
): Promise<IdentityBackup> {
	updateStep("decrypt", "in_progress");
	let decryptedData: IdentityBackup;
	try {
		decryptedData = (await decryptIdentityBackup(
			encryptedBackup,
			pwd
		)) as IdentityBackup;
		updateStep("decrypt", "completed");
	} catch (decryptError: unknown) {
		updateStep("decrypt", "error", "Incorrect password");
		logger.error(
			{
				operation: "decrypt_backup",
				errorType:
					decryptError instanceof Error ? decryptError.name : "UnknownError",
			},
			"Decryption failed"
		);
		if (
			decryptError instanceof Error &&
			decryptError.message.includes("Invalid passphrase")
		) {
			throw new Error("Incorrect password. Please try again.");
		}
		throw new Error("Unable to decrypt this backup. Please try again.");
	}

	if (!validateFn(decryptedData)) {
		throw new Error("Invalid backup format");
	}

	// Pre-0.3 backups: bsv-bap 0.3 changed identity derivation and is
	// intentionally not backward compatible. Recompute the identities under
	// the current scheme (same keys, NEW bapIds), persist the re-encrypted
	// backup, and reissue a fresh backup file — the old file is obsolete.
	const reissued =
		!isSeedIdentityBackup(decryptedData) &&
		(isLegacyBackup(decryptedData) || isType42Backup(decryptedData))
			? await reissueLegacyBackupIfNeeded(decryptedData, pwd)
			: null;
	if (reissued) {
		decryptedData = reissued.backup;
		logger.warn(
			{
				operation: "reissue_legacy_backup",
				recomputed: reissued.recomputed.length,
			},
			"Legacy backup upgraded to current identity derivation"
		);
		// The automatic download in reissueLegacyBackupIfNeeded can be blocked
		// by the browser when this runs outside a user gesture (e.g. the
		// auto-submitted set-password flow). Always offer an explicit,
		// gesture-driven re-download so the user can save the new file.
		toast.info(
			"Your backup used an older identity format. It's been upgraded — your keys are unchanged, but your identity IDs are new. Save the new backup file; the old one is obsolete.",
			{
				duration: Number.POSITIVE_INFINITY,
				action: {
					label: "Download backup",
					onClick: () =>
						downloadFile(reissued.encrypted, reissued.filename, "text/plain"),
				},
			}
		);
	}

	// Store decrypted backup in sessionStorage for identity extraction
	sessionStorage.setItem(
		STORAGE_KEYS.DECRYPTED_BACKUP,
		JSON.stringify(decryptedData)
	);

	return decryptedData;
}

/** Sign in with a decrypted backup, handling sync progress and result validation */
/** A sign-in failure that keeps the server's code so the UI can branch on it. */
export class LoginFlowError extends Error {
	readonly code: string | undefined;
	readonly retryable: boolean;
	readonly canEnroll: boolean;
	constructor(
		message: string,
		code?: string,
		retryable = false,
		canEnroll = true
	) {
		super(message);
		this.name = "LoginFlowError";
		this.code = code;
		this.retryable = retryable;
		this.canEnroll = canEnroll;
	}
}

/** The server refused because this identity has no Sigma account yet. */
export const UNREGISTERED_IDENTITY_CODE = "RESTORE_PROFILE_NOT_FOUND";

export async function performSignIn(
	decryptedData: IdentityBackup,
	updateStep: SyncStepUpdater,
	options?: { enrollNewProfile?: boolean }
): Promise<Awaited<ReturnType<typeof signInWithBackup>>> {
	const onSyncProgress = createSyncProgressCallback(updateStep);

	updateStep("complete", "in_progress");
	const result = await signInWithBackup(decryptedData, {
		onSyncProgress,
		...(options?.enrollNewProfile ? { enrollNewProfile: true } : {}),
	});

	if (!result.ok || result.error) {
		updateStep("complete", "error", result.error || "Sign-in failed");
		throw new LoginFlowError(
			result.error || "Authentication failed",
			result.errorCode,
			result.retryable === true,
			!isSeedIdentityBackup(decryptedData)
		);
	}

	updateStep("complete", "completed");
	return result;
}

/** Handle post-sign-in redirect for both OAuth and direct flows */
export function handlePostSignInRedirect(
	finalIdentity: { id: string },
	oauthParams: LoginOAuthParams,
	returnToParam: string | null,
	updateStep: SyncStepUpdater
): PostSignInResult {
	updateStep("redirect", "in_progress");

	if (notifySignerReturnIfRequested()) {
		updateStep("redirect", "completed");
		return "signer_return";
	}

	if (oauthParams.clientId && oauthParams.redirectUri) {
		const authorizeUrl = buildAuthorizeUrl(window.location.origin, {
			clientId: oauthParams.clientId,
			redirectUri: oauthParams.redirectUri,
			state: oauthParams.state,
			codeChallenge: oauthParams.codeChallenge,
			codeChallengeMethod: oauthParams.codeChallengeMethod,
			scope: oauthParams.scope,
			responseType: oauthParams.responseType,
		});
		window.location.href = authorizeUrl.toString();
		return "redirecting";
	}

	window.location.href =
		returnToParam || `/account/${encodeURIComponent(finalIdentity.id)}`;
	return "redirecting";
}

/** Encrypt and save an imported unencrypted backup */
export async function encryptAndSaveBackup(
	backup: IdentityBackup,
	pwd: string,
	confirmPwd: string,
	callbacks: {
		setLoading: (v: boolean) => void;
		setError: (e: string) => void;
		setImportedUnencryptedBackup: (b: IdentityBackup | null) => void;
		setShowSetPasswordForm: (v: boolean) => void;
		setNewPassword: (v: string) => void;
		setConfirmPassword: (v: string) => void;
		setHasLocalBackup: (v: boolean) => void;
		setPassword: (v: string) => void;
		setPendingEncryptedBackup: (value: string | null) => void;
		isCandidateCurrent?: () => boolean;
	}
): Promise<void> {
	if (pwd.length < MIN_PASSWORD_LENGTH) {
		callbacks.setError(
			`Password must be at least ${MIN_PASSWORD_LENGTH} characters`
		);
		return;
	}

	if (pwd !== confirmPwd) {
		callbacks.setError("Passwords do not match");
		return;
	}

	callbacks.setLoading(true);
	callbacks.setError("");

	try {
		const encrypted = await encryptIdentityBackup(
			validateIdentityBackup(backup),
			pwd
		);
		if (callbacks.isCandidateCurrent && !callbacks.isCandidateCurrent()) {
			return;
		}
		callbacks.setPendingEncryptedBackup(encrypted);

		callbacks.setImportedUnencryptedBackup(null);
		callbacks.setShowSetPasswordForm(false);
		callbacks.setNewPassword("");
		callbacks.setConfirmPassword("");
		callbacks.setHasLocalBackup(true);
		callbacks.setPassword(pwd);

		setTimeout(() => {
			const form = document.querySelector(
				"form[data-login-form]"
			) as HTMLFormElement;
			if (form) {
				form.requestSubmit();
			}
		}, AUTO_SUBMIT_DELAY);
	} catch {
		logger.error(
			{ operation: "encrypt_backup", errorType: "EncryptionError" },
			"Backup encryption failed"
		);
		callbacks.setError("Failed to encrypt wallet. Please try again.");
	} finally {
		callbacks.setLoading(false);
	}
}

function commitImportedBackup(
	candidate: IdentityBackup,
	encrypted: string,
	identity: { privateKey: string; bapId: string },
	priorLocal: string | null,
	priorSession: string | null,
	priorActive: string | null,
	priorSelected: string | null
): void {
	const previousInventory = localStorage.getItem(INCOMPLETE_INVENTORY_KEY);
	try {
		localStorage.setItem(STORAGE_KEYS.ENCRYPTED_BACKUP, encrypted);
		sessionStorage.setItem(
			STORAGE_KEYS.DECRYPTED_BACKUP,
			JSON.stringify(candidate)
		);
		sessionStorage.setItem(
			STORAGE_KEYS.ACTIVE_ACCOUNT,
			JSON.stringify({ wif: identity.privateKey, id: identity.bapId })
		);
		localStorage.setItem(STORAGE_KEYS.CURRENT_IDENTITY, identity.bapId);
		if (hasIncompleteInventory(candidate)) {
			localStorage.setItem(INCOMPLETE_INVENTORY_KEY, encrypted);
		} else {
			localStorage.removeItem(INCOMPLETE_INVENTORY_KEY);
		}
	} catch {
		let rollbackFailed = false;
		for (const [storage, key, value] of [
			[localStorage, STORAGE_KEYS.ENCRYPTED_BACKUP, priorLocal],
			[localStorage, INCOMPLETE_INVENTORY_KEY, previousInventory],
			[sessionStorage, STORAGE_KEYS.DECRYPTED_BACKUP, priorSession],
			[sessionStorage, STORAGE_KEYS.ACTIVE_ACCOUNT, priorActive],
			[localStorage, STORAGE_KEYS.CURRENT_IDENTITY, priorSelected],
		] as const) {
			try {
				if (value === null) {
					storage.removeItem(key);
				} else {
					storage.setItem(key, value);
				}
			} catch {
				rollbackFailed = true;
			}
		}
		throw new Error(
			rollbackFailed
				? "Import could not restore previous storage"
				: "Import could not save backup"
		);
	}
}

export async function authenticateImportedBackup(
	pendingEncryptedBackup: string,
	pwd: string,
	isCandidateCurrent: () => boolean = () => true,
	options?: { enrollNewProfile?: boolean; selectedBapId?: string }
) {
	const priorLocal = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	const priorSession = sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP);
	const priorActive = sessionStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT);
	const priorSelected = localStorage.getItem(STORAGE_KEYS.CURRENT_IDENTITY);
	const sourceUnchanged = () =>
		isCandidateCurrent() &&
		localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) === priorLocal &&
		sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) === priorSession &&
		sessionStorage.getItem(STORAGE_KEYS.ACTIVE_ACCOUNT) === priorActive &&
		localStorage.getItem(STORAGE_KEYS.CURRENT_IDENTITY) === priorSelected;
	const candidate = await decryptIdentityBackup(pendingEncryptedBackup, pwd);
	const identity = extractIdentityFromBackup(candidate, options?.selectedBapId);
	if (options?.enrollNewProfile && isSeedIdentityBackup(candidate)) {
		throw new LoginFlowError(
			"Imported seed profiles must already have an account. Create a new seed wallet through signup.",
			"SEED_ENROLLMENT_UNAVAILABLE"
		);
	}
	if (!sourceUnchanged()) {
		throw new Error("Backup import was superseded");
	}
	const result = await signInWithBackup(candidate, {
		bapId: identity.bapId,
		...(options?.enrollNewProfile
			? { enrollNewProfile: true }
			: { restoreExistingProfile: true }),
		persistLocalState: false,
		navigateOnRedirect: false,
	});
	if (!result.ok || result.error) {
		throw new LoginFlowError(
			result.error || "Backup identity is not registered",
			result.errorCode,
			result.retryable === true,
			!isSeedIdentityBackup(candidate)
		);
	}
	if (!sourceUnchanged()) {
		throw new Error("Backup import was superseded");
	}
	commitImportedBackup(
		candidate,
		pendingEncryptedBackup,
		identity,
		priorLocal,
		priorSession,
		priorActive,
		priorSelected
	);
	await preserveImportedVaultBackup(candidate);
	markPrfBackupStale();
	if (
		localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) !==
			pendingEncryptedBackup ||
		sessionStorage.getItem(STORAGE_KEYS.DECRYPTED_BACKUP) !==
			JSON.stringify(candidate)
	) {
		throw new Error("Backup import continuation was superseded");
	}
	try {
		markCloudBackupStale();
	} catch {
		/* Local commit remains valid if cloud marking fails. */
	}
	return { identity, redirectURI: result.redirectURI };
}

async function executeImportedBackupLogin(
	pendingEncryptedBackup: string,
	pwd: string,
	updateStep: SyncStepUpdater,
	oauthParams: LoginOAuthParams,
	returnToParam: string | null,
	isCandidateCurrent: () => boolean,
	options?: { enrollNewProfile?: boolean }
): Promise<PostSignInResult> {
	const { identity, redirectURI } = await authenticateImportedBackup(
		pendingEncryptedBackup,
		pwd,
		isCandidateCurrent,
		options
	);
	rememberSuccessfulSignIn("file");
	if (redirectURI) {
		window.location.href = redirectURI;
		return "redirecting";
	}
	return handlePostSignInRedirect(
		identity,
		oauthParams,
		returnToParam,
		updateStep
	);
}

/** Execute password-based login: decrypt, sign in, sync, redirect */
export async function executePasswordLogin(
	pwd: string,
	updateStep: SyncStepUpdater,
	oauthParams: LoginOAuthParams,
	returnToParam: string | null,
	pendingEncryptedBackupOrOptions?: string | { enrollNewProfile?: boolean },
	isCandidateCurrent: () => boolean = () => true,
	enrollmentOptions?: { enrollNewProfile?: boolean }
): Promise<PostSignInResult> {
	const options =
		enrollmentOptions ??
		(typeof pendingEncryptedBackupOrOptions === "object"
			? pendingEncryptedBackupOrOptions
			: undefined);
	let pendingEncryptedBackup =
		typeof pendingEncryptedBackupOrOptions === "string"
			? pendingEncryptedBackupOrOptions
			: undefined;
	if (!pendingEncryptedBackup && options?.enrollNewProfile) {
		pendingEncryptedBackup =
			localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP) ?? undefined;
	}
	if (pendingEncryptedBackup) {
		return executeImportedBackupLogin(
			pendingEncryptedBackup,
			pwd,
			updateStep,
			oauthParams,
			returnToParam,
			isCandidateCurrent,
			options
		);
	}
	const localBackup = localStorage.getItem(STORAGE_KEYS.ENCRYPTED_BACKUP);
	if (!localBackup) {
		throw new Error("No backup found. Please import a backup file.");
	}

	const decryptedData = await decryptAndValidateBackup(
		localBackup,
		pwd,
		updateStep,
		validateBackupStructure
	);

	const identity = extractIdentityFromBackup(decryptedData);
	logger.info({ operation: "sigma_sign_in" }, "Signing in with Sigma identity");

	const result = await performSignIn(decryptedData, updateStep, options);

	const finalIdentity = await postSignInIdentitySync(identity, pwd, result);
	completeRecoveryMethod();

	return handlePostSignInRedirect(
		finalIdentity,
		oauthParams,
		returnToParam,
		updateStep
	);
}

/** Build restore callback URL for OAuth flows */
export function buildRestoreCallbackURL(
	origin: string,
	searchParams: URLSearchParams,
	returnToParam: string | null
): string {
	if (!origin) {
		return "/restore";
	}

	const cId = searchParams.get("client_id");
	const rUri = searchParams.get("redirect_uri");

	if (cId && rUri) {
		const oauthUrl = new URL("/oauth2/authorize", origin);
		searchParams.forEach((value, key) => {
			oauthUrl.searchParams.set(key, value);
		});
		return `${origin}/restore?return_to=${encodeURIComponent(oauthUrl.toString())}`;
	}

	if (returnToParam) {
		return `${origin}/restore?return_to=${encodeURIComponent(returnToParam)}`;
	}

	return `${origin}/restore`;
}

/**
 * Decide where to send a freshly signed-in user who has no local backup.
 *
 * Mirrors the social sign-in flow's two-pronged routing:
 *   callbackURL: restoreCallbackURL  -> existing user -> /restore
 *   newUserCallbackURL: "/onboarding" -> new user     -> /onboarding
 *
 * Better Auth's emailOTP plugin does not expose callbackURL/newUserCallbackURL
 * options on signIn.emailOtp, so we replicate the branching client-side by
 * asking /api/backup/status. A 401 there means the user has a Better Auth
 * session but no sigma profile row yet (truly new account).
 */
export async function resolvePostEmailOtpRedirect(
	restoreCallbackURL: string
): Promise<string> {
	try {
		const statusRes = await fetchLoginBackupStatus();
		if (statusRes.status === 401) {
			return "/onboarding";
		}
	} catch {
		// Network blip — let /restore handle the missing-backup case downstream.
	}
	return restoreCallbackURL;
}
